import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Certificate } from '@app/_models/certificate';
import { RsaProvided } from '@app/_models/rsaProvided';
import { RsaProviderInfo } from '@app/_models/rsaProviderInfo';
import { StateCity } from '@app/_models/stateCity';
import { Subscription } from 'rxjs';
import { debounceTime, first } from 'rxjs/operators';
import { ConfigDataService } from '@app/_services/config-data.service';
import { AlertService } from '@app/_services';

@Component({
  selector: 'app-rsa-service-form',
  templateUrl: './rsa-service-form.component.html',
  styleUrls: ['./rsa-service-form.component.less']
})
export class RsaServiceFormComponent implements OnInit, OnDestroy {
  @Input() selectedCertificate: Certificate;
  @Input() stateList: string[] = [];
  @Input() cityList: string[] = [];
  @Input() stateCityList: StateCity[] = [];
  @Input() loading = false;
  @Input() providerList: string[] = [];
  @Input() submitted = false;
  @Input() rsaProvidedForm: UntypedFormGroup;
  @Input() selectedProviderContactNumber: string = '';
  @Input() newHistoryCommentControl: UntypedFormControl;
  
  @Output() formSubmit = new EventEmitter<void>();
  @Output() copyToClipboard = new EventEmitter<string>();
  @Output() providerChange = new EventEmitter<string>();
  @Output() autoFillLocation = new EventEmitter<void>();
  @Output() copyCustomerInfo = new EventEmitter<void>();
  @Output() formCancel = new EventEmitter<void>();
  
  constructor(
    private formBuilder: UntypedFormBuilder,
    private configDataService: ConfigDataService,
    private alertService: AlertService
  ) { }

  ngOnInit() {
    // If the form is not passed from parent, create one locally
    if (!this.rsaProvidedForm) {
      this.createLocalForm();
    }
    
    // Create local control if not passed from parent
    if (!this.newHistoryCommentControl) {
      this.newHistoryCommentControl = new UntypedFormControl('');
    }
  }
  
  ngOnDestroy() {
    // Clean up any subscriptions
  }
  
  private createLocalForm() {
    this.rsaProvidedForm = this.formBuilder.group({
      // Customer Data
      state: ['', Validators.required],
      city: ['', Validators.required],
      callerName: [''],
      callbackNumber: [''],
      // RSA Provider & Dealership Data
      dealershipLocation: ['', Validators.required],
      provider: ['', Validators.required],
      // If provider is 'Other', show reimbursementAmount and reimbursementComment
      reimbursementAmount: [''],
      reimbursementComment: [''],
      // Financials
      distance: ['', [Validators.required, Validators.pattern('^[0-9]+(\\.[0-9]+)?$')]],
      serviceCost: ['', [Validators.required, Validators.pattern('^[0-9]+(\\.[0-9]+)?$')]],
      serviceStatus: ['In progress', Validators.required],
      paidBy: ['', Validators.required],
      // Service meta
      rsaProvidedDate: [new Date().toISOString().substring(0, 16)],
      rsaProvideInstanceNumber: [0],
      certificateNumber: [''],
      // History
      history: [[]]
    });
  }
  
  get rsaF() { return this.rsaProvidedForm.controls; }
  
  onSubmit() {
    this.formSubmit.emit();
  }
  
  onCopyToClipboard(text: string) {
    this.copyToClipboard.emit(text);
  }
  
  onProviderChange(provider: string) {
    this.providerChange.emit(provider);
  }
  
  onAutoFillLocation() {
    this.autoFillLocation.emit();
  }
  
  onCopyCustomerInfo() {
    this.copyCustomerInfo.emit();
  }
  
  // Add missing methods that are referenced in the HTML template
  onProviderSelection(provider: string) {
    // This method forwards the provider selection to the parent component
    this.providerChange.emit(provider);
  }
  
  onCancel() {
    // This method emits the cancel event to the parent component
    this.formCancel.emit();
  }
}
